import { useEffect } from 'react';
import {
  useQuery, useQueryClient, UseQueryResult, useInfiniteQuery, UseInfiniteQueryResult, InfiniteData,
} from '@tanstack/react-query';
import { getAlerts } from '../../services/AlertsService';
import { AlertsResponse } from '../../types/Alerts';
import { FeaturesManagement } from '../../../featuresControl';
import { mergeNewestIntoFirstPage } from '../../../utils/queryFilters';

function useGetAlerts(fleetId: string, query: string, enabled = true, refetch = true): UseQueryResult<AlertsResponse> {
  return useQuery(
    ['alerts', fleetId, query],
    async (): Promise<AlertsResponse> => getAlerts(fleetId, query),
    {
      retry: 1,
      retryDelay: 3000,
      refetchInterval: refetch ? FeaturesManagement.productivity.refetchInterval : false,
      keepPreviousData: true,
      enabled: !!enabled,
    },
  );
}

export function useGetAlertsLazy(fleetId: string, payload?: any, query?: string, deps?: any): UseInfiniteQueryResult<AlertsResponse> {
  const queryClient = useQueryClient();
  const queryKey = ['alerts-lazy', fleetId, deps];

  const infiniteQuery = useInfiniteQuery(
    queryKey,
    async ({ pageParam = query }): Promise<AlertsResponse> => getAlerts(fleetId, pageParam),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) => (lastPage?.total ? ((lastPage.limit + lastPage.skip + 1) < lastPage.total) : undefined),
    },
  );

  // Polls only the newest page in a plain (non-infinite) query, then splices any new items into
  // the top of the already-loaded first page. This keeps auto-refresh cheap and non-destructive
  // regardless of how many pages the user has scrolled through.
  const { data: latestPage } = useQuery(
    ['alerts-lazy-latest', fleetId, deps, query],
    async (): Promise<AlertsResponse> => getAlerts(fleetId, query as string),
    {
      retry: 1,
      retryDelay: 3000,
      refetchOnWindowFocus: false,
      refetchInterval: FeaturesManagement.productivity.refetchIntervalLazyLists,
      enabled: !!infiniteQuery.data,
    },
  );

  useEffect(() => {
    if (!latestPage) return;
    queryClient.setQueryData<InfiniteData<AlertsResponse>>(
      queryKey,
      (existing) => mergeNewestIntoFirstPage(existing, latestPage),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestPage]);

  return infiniteQuery;
}

export default useGetAlerts;
